/** @jsx jsx */
import React from 'react';
import { styled, css, jsx } from '@compiled/react';
// @ts-expect-error - TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'RowType'. Did you mean to use 'import RowType from "@atlaskit/dynamic-table"' instead? | TS2614 - Module '"@atlaskit/dynamic-table"' has no exported member 'HeadType'. Did you mean to use 'import HeadType from "@atlaskit/dynamic-table"' instead?
import type { RowType, HeadType } from '@atlaskit/dynamic-table';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl';
import type { IssueKey } from '@atlassian/jira-shared-types/src/general.tsx';
import commonMessages from '../../common/messages';
import type { IssueTableData, IssueTableRow } from '../../common/types';
import { useFormatDurationFromSeconds } from '../../common/utils/date-range';
import { useHoveredIssueKey } from '../../controllers/hovered-item';
import { CycleTimeCellContent } from './cycle-time-cell-content';
import { IssueCellContent } from './issue-cell-content';
import messages from './messages';

export const useTableHeader = (): HeadType => {
	const { formatMessage } = useIntl();

	return {
		cells: [
			{
				key: 'cycleTime',
				content: formatMessage(messages.headerCycleTime),
				isSortable: true,
				width: 120,
			},
			{
				key: 'averageReviewTime',
				content: formatMessage(messages.headerReviewTime),
				isSortable: true,
				width: 120,
			},
			{
				key: 'issue',
				content: formatMessage(messages.headerIssue),
				isSortable: true,
				width: 452,
			},
			{
				key: 'pullRequests',
				content: formatMessage(messages.headerPullRequests),
				isSortable: true,
				width: 112,
			},
			{
				key: 'commits',
				content: formatMessage(messages.headerCommits),
				isSortable: true,
				width: 112,
			},
			{
				key: 'dateDeployed',
				content: formatMessage(messages.headerDateDeployed),
				isSortable: true,
				width: 112,
			},
		],
	};
};

// count issues with a cycle time greater than threshold
export const countRowsExceedingCycleTime = (data: IssueTableData): { [key: string]: number } => {
	const cycleTimes: { [key: string]: number } = {
		threeMonths: 7890000,
		sixMonths: 15780000,
		oneYear: 31560000,
		twoYears: 63120000,
	};

	const initialCounts: { [key: string]: number } = {
		threeMonths: 0,
		sixMonths: 0,
		oneYear: 0,
		twoYears: 0,
	};

	const acc = { ...initialCounts };

	data?.rows.forEach((row) => {
		for (const period in cycleTimes) {
			if (row.cycleTime > cycleTimes[period]) {
				acc[period] += 1;
			}
		}
	});

	const counts = acc;

	return counts;
};

export const useTableRows = (
	data: IssueTableRow[] | null | undefined,
	cycleTimeStatistic: number | null,
	onIssueClicked: (issueKey: IssueKey) => void,
): RowType[] => {
	const { formatMessage, formatDate } = useIntl();
	const formatDurationFromSeconds = useFormatDurationFromSeconds();
	const [, actions] = useHoveredIssueKey();
	if (data == null) {
		return [];
	}

	return data.map(
		({
			cycleTime,
			issue,
			numCommits,
			numPullRequests,
			reviewTime,
			dateDeployed,
		}: IssueTableRow) => ({
			'data-issue-key': issue.key,
			// @ts-expect-error - TS7006 - Parameter 'evt' implicitly has an 'any' type.
			onMouseEnter: (evt) => {
				actions.setHoveredIssueKey(evt.currentTarget.dataset.issueKey);
			},
			onMouseLeave: () => {
				actions.setHoveredIssueKey(null);
			},

			// NOTE - cells are sorted based on the value of key
			cells: [
				{
					key: `${cycleTime} ${issue.key}`,
					content: (
						<CycleTimeCellContent
							cycleTimeStatistic={cycleTimeStatistic}
							issueCycleTime={cycleTime}
						/>
					),
				},
				{
					key: `${reviewTime ?? 0} ${issue.key}`,
					content:
						reviewTime === null ? (
							<Tooltip content={formatMessage(messages.noReviewTime)} position="right">
								<NoReviewTimeSpan>-</NoReviewTimeSpan>
							</Tooltip>
						) : (
							<div css={truncateTextStyles}>
								{formatDurationFromSeconds(reviewTime, {
									days: 'short',
									hours: 'short',
								})}
							</div>
						),
				},
				{
					key: issue.key,
					content: <IssueCellContent issue={issue} onIssueClicked={onIssueClicked} />,
				},
				{
					key: `${numPullRequests} ${issue.key}`,
					content: (
						<div css={truncateTextStyles}>
							{formatMessage(commonMessages.pullRequestsCountShort, {
								value: numPullRequests,
							})}
						</div>
					),
				},
				{
					key: `${numCommits} ${issue.key}`,
					content: (
						<div css={truncateTextStyles}>
							{formatMessage(commonMessages.commitCount, { value: numCommits })}
						</div>
					),
				},
				{
					key: `${dateDeployed.getTime()} ${issue.key}`,
					content: (
						<Tooltip
							content={formatDate(dateDeployed, {
								year: 'numeric',
								month: 'long',
								day: 'numeric',
								hour: 'numeric',
								minute: 'numeric',
							})}
							position="top-start"
						>
							<div css={truncateTextStyles}>
								{formatDate(dateDeployed, {
									month: 'short',
									day: 'numeric',
								})}
							</div>
						</Tooltip>
					),
				},
			],
		}),
	);
};

const truncateTextStyles = css({
	textOverflow: 'ellipsis',
	overflow: 'hidden',
	whiteSpace: 'nowrap',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const NoReviewTimeSpan = styled.span({
	padding: token('space.200', '16px'),
});
